import { useState } from 'react';
import { useTemplateConfig } from '@/hooks/useTemplateConfig';
import { useCompanyData } from '@/hooks/useCompanyData';
import { useTemplatePresets } from '@/hooks/useTemplatePresets';
import { showToast } from '@/components/Toast';
import { Link } from 'react-router-dom';
import AdminSidebar, { type AdminSection } from './components/AdminSidebar';
import AdminForms from './components/AdminForms';
import AdminPreview from './components/AdminPreview';

export default function AdminTemplatePage() {
  const [activeSection, setActiveSection] = useState<AdminSection>('company');
  const { config, updateConfig, resetToDefault } = useTemplateConfig();
  const { data: companyData, updateData: updateCompany, resetToDefault: resetCompany } = useCompanyData();
  const { presets, activeId, setActive, togglePresetActive, savePreset, resetToDefault: resetPresets } = useTemplatePresets();

  const handleReset = () => {
    if (window.confirm('Deseja restaurar todos os valores padrão?')) {
      resetToDefault();
      resetCompany();
      resetPresets();
      showToast('Valores restaurados para o padrão.', 'info');
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex flex-col">
      {/* Header */}
      <header className="h-14 bg-brand-darker border-b border-brand-gold/10 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-settings-3-line text-brand-gold text-xl" />
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm">Painel Administrativo</h1>
            <p className="text-brand-text-muted text-xs">Template da Proposta</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-brand-text-muted text-xs hidden sm:block">
            Salvo automaticamente no navegador
          </span>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 text-brand-text-muted hover:text-red-400 text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-red-400/30 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            <span className="hidden sm:inline">Restaurar Padrão</span>
          </button>
          <Link
            to="/admin/library"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-folder-image-line" />
            <span className="hidden sm:inline">Biblioteca</span>
          </Link>
          <Link
            to="/admin/proposals"
            className="flex items-center gap-2 text-brand-text-muted hover:text-brand-gold text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/10 hover:border-brand-gold/30 cursor-pointer"
          >
            <i className="ri-file-list-3-line" />
            <span className="hidden sm:inline">Propostas</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors px-3 py-1.5 rounded-md border border-brand-gold/20 hover:bg-brand-gold/10 cursor-pointer"
          >
            <i className="ri-home-line" />
            <span className="hidden sm:inline">Portal</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        <AdminSidebar active={activeSection} onChange={setActiveSection} />

        <div className="flex-1 flex overflow-hidden">
          {/* Forms Panel */}
          <div className="flex-1 overflow-y-auto p-5">
            <div className="max-w-2xl">
              <AdminForms
                activeSection={activeSection}
                templateConfig={config}
                companyData={companyData}
                presets={presets}
                activePresetId={activeId}
                onUpdateTemplate={updateConfig}
                onUpdateCompany={updateCompany}
                onSetActivePreset={setActive}
                onTogglePreset={togglePresetActive}
                onSavePreset={savePreset}
              />
            </div>
          </div>

          {/* Preview Panel */}
          <div className="w-[480px] xl:w-[560px] flex-shrink-0 border-l border-brand-gold/10 p-4 overflow-hidden hidden lg:flex">
            <AdminPreview config={config} companyData={companyData} presets={presets} activePresetId={activeId} />
          </div>
        </div>
      </div>
    </div>
  );
}